/**
 * Which month's tooltip the Revenue chart shows (ops insights Phase 2, ticket 05): a pure
 * reducer, so the rules are unit-tested without a DOM (`RevenueChart` holds it in
 * `useReducer`).
 *
 * - The pointer and keyboard focus each point at a month, or none. The pointer wins while both
 *   do: it is where the eye is.
 * - Keyboard focus counts only when it is VISIBLE (`:focus-visible`): a click also focuses the
 *   button, and a tooltip opened by the click would stay open after the pointer left. The
 *   component decides that before dispatching `focus`.
 * - Escape hides the open tooltip without moving either (WCAG 1.4.13, dismissible), and the
 *   dismissal HOLDS for that month: leaving it and coming back does not reopen it, until a
 *   different month is pointed at or focused.
 */

export interface ChartTip {
  /** The month under the pointer, by index. */
  hovered: number | null;
  /** The month with visible keyboard focus, by index. */
  focused: number | null;
  /** The month whose tooltip Escape hid, until another month is shown. */
  dismissed: number | null;
}

export type ChartTipEvent =
  | { type: 'hover'; index: number | null }
  | { type: 'focus'; index: number | null }
  | { type: 'escape' };

export const NO_TIP: ChartTip = { hovered: null, focused: null, dismissed: null };

/** The month whose tooltip is open, or null. */
export function openTip(state: ChartTip): number | null {
  const pointedAt = state.hovered ?? state.focused;
  return pointedAt === state.dismissed ? null : pointedAt;
}

export function chartTip(state: ChartTip, event: ChartTipEvent): ChartTip {
  if (event.type === 'escape') {
    const open = openTip(state);
    return open === null ? state : { ...state, dismissed: open };
  }
  const next =
    event.type === 'hover'
      ? { ...state, hovered: event.index }
      : { ...state, focused: event.index };
  // Pointing at ANOTHER month ends the dismissal; leaving one (null) does not.
  const pointedAt = next.hovered ?? next.focused;
  return pointedAt !== null && pointedAt !== state.dismissed
    ? { ...next, dismissed: null }
    : next;
}
